import { useMutation } from "@apollo/client";
import type { Interpolation } from "@emotion/serialize";
import { useAmplitude } from "../../../lib/amplitude/Amplitude";
import type { IReactableFieldsFragment } from "../../../lib/graphql/fragments/__generated__/ReactableFields.generated";
import useSelfie from "../../../lib/hooks/useSelfie";
import type { IReactionSubject } from "../../../types/graphqlTypes";
import { IReactionContent } from "../../../types/graphqlTypes";
import * as palette from "../../palette";
import AddReaction from "../../reactions/mutations/AddReaction";
import type {
  IAddReactionMutation,
  IAddReactionMutationVariables,
} from "../../reactions/mutations/__generated__/AddReaction.generated";
import RemoveReaction from "../../reactions/mutations/RemoveReaction";
import type {
  IRemoveReactionMutation,
  IRemoveReactionMutationVariables,
} from "../../reactions/mutations/__generated__/RemoveReaction.generated";
import ThumbsUpIcon from "../../icons/ThumbsUpIcon";

interface IThumbsUpReactionProps {
  subjectId: string;
  subjectType: IReactionSubject;
  reactable: IReactableFieldsFragment;
}

export default function ThumbsUpReaction({ subjectId, subjectType, reactable }: IThumbsUpReactionProps) {
  const viewer = useSelfie();
  const amplitude = useAmplitude();

  const [addReaction, { loading: isAddingReaction }] = useMutation<IAddReactionMutation, IAddReactionMutationVariables>(
    AddReaction,
  );

  const [removeReaction, { loading: isRemovingReaction }] = useMutation<
    IRemoveReactionMutation,
    IRemoveReactionMutationVariables
  >(RemoveReaction);

  const thumbsUpReactionGroup = reactable.reactionGroups.find(({ content }) => content === IReactionContent.ThumbsUp);

  if (!reactable.viewerCanReact) {
    const totalReactions = thumbsUpReactionGroup ? thumbsUpReactionGroup.totalReactions : 0;

    if (totalReactions === 0) {
      return null;
    }

    return (
      <div
        aria-label={totalReactions === 1 ? "1 pessoa curtiu" : `${totalReactions} pessoas curtiram`}
        css={{
          display: "inline-flex",
          boxSizing: "border-box",
          margin: 0,
          padding: "6px 8px 7px 8px",
          backgroundColor: palette.selago,
          border: "solid 1px transparent",
          borderRadius: 6,
          userSelect: "none",
        }}
      >
        <ThumbsUpIcon fill={palette.royalBlue} />

        <span
          css={{
            marginTop: -1,
            marginLeft: 8,
            color: palette.royalBlue,
            fontWeight: 700,
            fontSize: 12,
            lineHeight: 1.56,
            letterSpacing: 0.86,
          }}
        >
          {totalReactions}
        </span>
      </div>
    );
  }

  const viewerHasReacted = thumbsUpReactionGroup ? thumbsUpReactionGroup.viewerHasReacted : false;

  let conditionalButtonCssEffects: Interpolation<any> = {
    "&:hover, &:focus": {
      outline: "none",
      border: `solid 1px ${palette.portage}`,
    },
  };

  if (viewerHasReacted) {
    conditionalButtonCssEffects = {
      "&:hover": {
        opacity: 0.8,
      },
      "&:focus": {
        border: `solid 1px ${palette.mariner}`,
        outline: "none",
        boxShadow: `${palette.tropicalBlue} 0px 0px 0px 3px`,
      },
    };
  }

  const isButtonDisabled = !reactable.viewerCanReact || isAddingReaction || isRemovingReaction;
  const totalReactions = thumbsUpReactionGroup ? thumbsUpReactionGroup.totalReactions : 0;

  return (
    <button
      type="button"
      aria-label={totalReactions === 1 ? "1 pessoa curtiu" : `${totalReactions} pessoas curtiram`}
      disabled={isButtonDisabled}
      onClick={() => {
        if (isAddingReaction || isRemovingReaction) {
          return;
        }

        if (!viewer) {
          return;
        }

        if (viewerHasReacted) {
          amplitude.logEvent("Removed a reaction", {
            isPremium: viewer.isPremium,
            reactionContent: IReactionContent.ThumbsUp,
            subjectType,
          });

          removeReaction({
            variables: {
              input: {
                content: IReactionContent.ThumbsUp,
                subjectId,
                subjectType,
              },
            },
          });
        } else {
          amplitude.logEvent("Added a reaction", {
            isPremium: viewer.isPremium,
            reactionContent: IReactionContent.ThumbsUp,
            subjectType,
          });

          addReaction({
            variables: {
              input: {
                content: IReactionContent.ThumbsUp,
                subjectId,
                subjectType,
              },
            },
          });
        }
      }}
      css={{
        display: "flex",
        boxSizing: "border-box",
        margin: 0,
        padding: "6px 8px 7px 8px",
        backgroundColor: viewerHasReacted ? palette.mariner : palette.selago,
        border: "solid 1px transparent",
        borderRadius: 6,
        cursor: "pointer",
        ...conditionalButtonCssEffects,
      }}
    >
      <ThumbsUpIcon fill={viewerHasReacted ? palette.mariner : palette.royalBlue} />

      <span
        css={{
          marginTop: -1,
          marginLeft: 8,
          color: viewerHasReacted ? palette.white : palette.royalBlue,
          fontWeight: 700,
          fontSize: 12,
          lineHeight: 1.56,
          letterSpacing: 0.86,
        }}
      >
        {totalReactions}
      </span>
    </button>
  );
}
